import React, { useState, useEffect } from "react";
import * as api from "../api";
import { STATUSES, STATUSES_SUR_PLACE } from "../constants";
import TRANSLATIONS from "../translations";
import { subscribeToPush, qrDataUrl } from "../utils";
import { jsPDF } from "jspdf";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import {
  Btn, Card, Badge, Chip, Ref, Callout, EmptyState, SectionHeader,
  Textarea, QRCanvas, StatusTimeline, LoyaltyProgress,
} from "./UI";
import { CollectorMap } from "./Map";

function Track({ setView, initialId }) {
  const t = TRANSLATIONS.fr;
  const [orderId, setOrderId] = useState(initialId || "");
  const [order, setOrder]     = useState(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [pushStatus, setPushStatus] = useState("idle");
  // Inline review
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [branding, setBranding] = useState({});

  useEffect(() => {
    if (initialId) handleSearch(initialId);
    api.getBranding().then(d => { if (d) setBranding(d); }).catch(() => {});
  }, []); // eslint-disable-line

  // Auto-refresh order status every 30 seconds when tracking is active
  useEffect(() => {
    if (!order || !["En attente", "Réceptionné", "Collecté", "En traitement", "Prêt"].includes(order.status)) return;
    const interval = setInterval(async () => {
      try {
        const o = await api.getOrder(order.id);
        setOrder(o);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [order?.id, order?.status]); // eslint-disable-line

  async function handleSearch(id) {
    const sid = (id || orderId).trim().toUpperCase();
    if (!sid) { setError(t.fillFields); return; }
    setLoading(true); setError(""); setLoyalty(null);
    try {
      const o = await api.getOrder(sid);
      setOrder(o);
      // Fetch loyalty info
      if (o.phone) {
        try {
          const l = await api.getLoyalty(o.phone);
          setLoyalty(l);
        } catch { /* no loyalty */ }
      }
    } catch (err) {
      setError(t.notFound);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }

  async function handlePushSubscribe() {
    if (!order?.phone) return;
    setPushStatus("subscribing");
    const ok = await subscribeToPush(order.phone);
    setPushStatus(ok ? "subscribed" : "denied");
  }

  // Auto-subscribe to push if permission already granted
  useEffect(() => {
    if (order?.phone && "Notification" in window && Notification.permission === "granted" && pushStatus === "idle") {
      subscribeToPush(order.phone).then(ok => setPushStatus(ok ? "subscribed" : "idle")).catch(() => {});
    }
  }, [order?.phone]); // eslint-disable-line

  async function generatePDF() {
    const items = order.items ? order.items.split(",").filter(Boolean) : [];
    const qrImg = await qrDataUrl(order.id);
    const doc = new jsPDF({ unit: "mm", format: "a5" });
    const w = doc.internal.pageSize.getWidth();
    let y = 15;

    // Header
    doc.setFillColor(15, 45, 110);
    doc.rect(0, 0, w, 32, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20); doc.setFont(undefined, "bold");
    doc.text("Setal Express", w / 2, y, { align: "center" }); y += 7;
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    doc.text("Pressing Premium — Dakar, Sénégal", w / 2, y, { align: "center" }); y += 6;
    doc.setFontSize(9);
    doc.text(`Reçu N° ${order.id}`, w / 2, y, { align: "center" }); y += 12;

    // Status badge
    const statusColors = { "En attente": [245,158,11], "Réceptionné": [245,158,11], "Collecté": [59,130,246], "En traitement": [168,85,247], "Prêt": [34,197,94], "Livré": [26,86,219], "Récupéré": [5,150,105] };
    const sc = statusColors[order.status] || [100,100,100];
    doc.setFillColor(sc[0], sc[1], sc[2]);
    const badgeW = doc.getTextWidth(order.status) + 12;
    doc.roundedRect((w - badgeW) / 2, y - 4, badgeW, 7, 3, 3, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont(undefined, "bold");
    doc.text(order.status, w / 2, y, { align: "center" }); y += 12;

    // Info rows
    doc.setTextColor(55, 65, 81); doc.setFontSize(10);
    const rows = [
      ["Client", order.client],
      ["Téléphone", order.phone],
      ["Adresse", order.address || "—"],
      ["Date collecte", `${order.date} à ${order.time}`],
    ];
    if (order.delivery_date) rows.push(["Livraison", `${order.delivery_date} — ${order.delivery_time || ""}`]);

    for (const [label, val] of rows) {
      doc.setFont(undefined, "bold"); doc.text(label, 12, y);
      doc.setFont(undefined, "normal");
      const maxW = w - 12 - doc.getTextWidth(label) - 14;
      const lines = doc.splitTextToSize(val || "—", maxW);
      doc.text(lines, w - 12, y, { align: "right" });
      y += lines.length * 5 + 3;
      doc.setDrawColor(230, 230, 230); doc.line(12, y - 2, w - 12, y - 2);
    }
    y += 4;

    // Articles
    if (items.length) {
      doc.setFont(undefined, "bold"); doc.setFontSize(11);
      doc.text("Articles", 12, y); y += 6;
      doc.setFont(undefined, "normal"); doc.setFontSize(10);
      for (const item of items) {
        doc.text(`• ${item.trim()}`, 16, y); y += 5;
      }
      y += 4;
    }

    // Total
    if (order.price) {
      doc.setDrawColor(15, 45, 110); doc.setLineWidth(0.5); doc.line(12, y, w - 12, y); y += 7;
      doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.setTextColor(26, 86, 219);
      doc.text(`Total : ${Number(order.price).toLocaleString()} FCFA`, w / 2, y, { align: "center" }); y += 5;

      // Payment status
      if (order.payment_status && order.payment_status !== "pending") {
        y += 4;
        doc.setFontSize(10);
        if (order.payment_status === "paye") { doc.setTextColor(22, 163, 74); doc.text("Payé intégralement", w / 2, y, { align: "center" }); }
        else if (order.payment_status === "avance") {
          doc.setTextColor(217, 119, 6);
          doc.text(`Avance : ${Number(order.amount_paid || 0).toLocaleString()} FCFA — Reste : ${(Number(order.price) - Number(order.amount_paid || 0)).toLocaleString()} FCFA`, w / 2, y, { align: "center" });
        } else { doc.setTextColor(220, 38, 38); doc.text("Non payé", w / 2, y, { align: "center" }); }
      }
      y += 8;
    }

    // QR Code
    if (qrImg) {
      y += 2;
      doc.addImage(qrImg, "PNG", (w - 30) / 2, y, 30, 30); y += 34;
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text("Scannez pour suivre votre commande", w / 2, y, { align: "center" }); y += 8;
    }

    // Footer
    doc.setFontSize(9); doc.setTextColor(148, 163, 184);
    doc.text("Merci de votre confiance — Setal Express", w / 2, y, { align: "center" }); y += 4;
    doc.text("Tel: +221 78 123 45 67 | setalexpress.com", w / 2, y, { align: "center" });

    return doc;
  }

  async function downloadPDF() {
    const doc = await generatePDF();
    doc.save(`Recu_SetalExpress_${order.id}.pdf`);
  }

  async function sharePDF() {
    const doc = await generatePDF();
    const blob = doc.output("blob");
    const file = new File([blob], `Recu_SetalExpress_${order.id}.pdf`, { type: "application/pdf" });
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({ title: `Reçu Setal Express ${order.id}`, files: [file] }).catch(() => {});
    } else {
      // Fallback: download
      doc.save(`Recu_SetalExpress_${order.id}.pdf`);
    }
  }

  const orderStatuses = order?.order_type === "sur_place" ? STATUSES_SUR_PLACE : STATUSES;
  const statusIdx = order ? orderStatuses.indexOf(order.status) : -1;

  // Contact — actions partagées (appel / WhatsApp)
  function callShop() {
    window.location.href = `tel:${(branding.phone_main || "+221768090928").replace(/\s/g, "")}`;
  }
  function whatsappShop() {
    window.open(`https://wa.me/${branding.phone_whatsapp || "221768090928"}?text=${encodeURIComponent(`Bonjour, j'ai une question sur ma commande ${order.id}`)}`, "_blank");
  }

  const payTone = order?.payment_status === "paye" ? "success"
    : order?.payment_status === "avance" ? "warning" : "danger";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, paddingBottom: 78 }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, color: C.inkOn, padding: "16px 20px 22px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <button onClick={() => setView("landing")} className="btn-bounce" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,.10)", border: "1px solid rgba(255,255,255,.18)",
            color: C.inkOn, borderRadius: R.md, padding: "5px 11px",
            fontSize: F.size.base, fontWeight: F.weight.medium, marginBottom: 12,
          }}>
            <Icon name="back" size={14} />
            {t.back}
          </button>
          <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>
            {t.trackTitle}
          </h1>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: 20 }}>
        {/* ─── Recherche ─────────────────────────────────────────────────── */}
        <Card>
          <label htmlFor="track-id" style={{
            display: "block", fontSize: 13, fontWeight: 500, color: "#3f4a58", marginBottom: 5,
          }}>
            Numéro de commande
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {/* Repère court : en monospace majuscule, un libellé long est tronqué
                sur mobile. Le libellé accessible reste, lui, explicite. */}
            <input
              id="track-id"
              className="field mono"
              aria-label={t.trackPlaceholder}
              value={orderId}
              onChange={e => setOrderId(e.target.value.toUpperCase())}
              placeholder="CMD123456"
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              style={{ flex: 1, minWidth: 0, textTransform: "uppercase" }}
            />
            <Btn icon="search" onClick={() => handleSearch()} disabled={loading} loading={loading}
              style={{ flexShrink: 0 }}>
              {t.searchBtn}
            </Btn>
          </div>
          {error && <Callout tone="danger" style={{ marginTop: 12 }}>{error}</Callout>}
        </Card>

        {/* Commande introuvable */}
        {error && !order && (
          <Card style={{ marginTop: 12 }} padded={false}>
            <EmptyState
              icon="search"
              title="Commande introuvable"
              description="Vérifiez le numéro figurant sur votre reçu, ou scannez le QR code de votre ticket."
            />
          </Card>
        )}

        {order && (
          <>
            {/* ─── Suivi ─────────────────────────────────────────────────── */}
            <Card className="animate-fadeInUp" style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: F.size.sm, color: C.inkMuted }}>{t.order}</p>
                  <Ref style={{ fontSize: F.size.xl, fontWeight: F.weight.semi }}>{order.id}</Ref>
                </div>
                <Badge status={order.status} />
              </div>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                <StatusTimeline statuses={orderStatuses} currentStatus={order.status} />
              </div>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                {[
                  ["user", t.client, order.client],
                  ["phone", t.telephone, order.phone],
                  ["calendar", t.date, `${order.date} à ${order.time}`],
                ].map(([iconName, k, v]) => (
                  <div key={k} style={{
                    display: "flex", gap: 10, alignItems: "flex-start",
                    fontSize: F.size.base, marginBottom: 8,
                  }}>
                    <span style={{ color: C.inkFaint, marginTop: 2 }}><Icon name={iconName} size={14} /></span>
                    <span style={{ fontWeight: F.weight.medium, color: C.inkBody, minWidth: 76 }}>{k}</span>
                    <span style={{ color: C.ink, flex: 1, minWidth: 0, ...numeric }}>{v}</span>
                  </div>
                ))}

                {order.items && (
                  <div style={{ marginTop: 12 }}>
                    <p style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 6 }}>
                      {t.articles}
                    </p>
                    {order.items.split(",").filter(Boolean).map((item, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        fontSize: F.size.base, color: C.inkBody, padding: "3px 0",
                      }}>
                        <Icon name="garment" size={13} color={C.inkFaint} />
                        <span style={numeric}>{item.trim()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {order.price && (
                  <div style={{
                    display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`,
                  }}>
                    <span style={{ fontSize: F.size.base, color: C.inkMuted }}>{t.total}</span>
                    <span style={{ fontSize: F.size.xl, fontWeight: F.weight.bold, color: C.ink, ...numeric }}>
                      {order.price.toLocaleString()} FCFA
                    </span>
                  </div>
                )}

                {/* État du paiement */}
                {order.payment_status && order.payment_status !== "pending" && (
                  <Callout
                    tone={payTone}
                    icon={order.payment_status === "paye" ? "success" : order.payment_status === "avance" ? "coins" : "alert"}
                    style={{ marginTop: 12 }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <span style={{ fontWeight: F.weight.semi, color: C.ink }}>
                        {order.payment_status === "paye" ? "Payé" : order.payment_status === "avance" ? "Avance versée" : "Non payé"}
                      </span>
                      {order.payment_status === "avance" && order.amount_paid && (
                        <span style={{ fontWeight: F.weight.semi, color: C.warning, ...numeric }}>
                          {Number(order.amount_paid).toLocaleString()} FCFA
                        </span>
                      )}
                    </div>
                    {order.payment_status === "avance" && order.price && order.amount_paid && (
                      <div style={{ marginTop: 3, ...numeric }}>
                        Reste à payer : <strong>{(Number(order.price) - Number(order.amount_paid)).toLocaleString()} FCFA</strong>
                      </div>
                    )}
                  </Callout>
                )}
              </div>
            </Card>

            {/* ─── Collecteur en route ───────────────────────────────────── */}
            {order.status === "En attente" && order.collector_status === "en_route" && (
              <Callout tone="warning" icon="collect" title="Collecteur en route" style={{ marginTop: 12 }}>
                <div>Votre collecteur se dirige vers vous. Suivez-le en temps réel ci-dessous.</div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Btn small variant="white" icon="phone" onClick={callShop} style={{ flex: 1 }}>Appeler</Btn>
                  <Btn small variant="white" icon="chat" onClick={whatsappShop} style={{ flex: 1 }}>WhatsApp</Btn>
                </div>
              </Callout>
            )}

            {/* ─── Collecteur arrivé ─────────────────────────────────────── */}
            {order.status === "En attente" && order.collector_status === "arrived" && (
              <Callout tone="success" icon="location" title="Collecteur arrivé" style={{ marginTop: 12 }}>
                Le collecteur est à votre adresse. Merci de lui remettre votre linge.
              </Callout>
            )}

            {/* ─── Linge réceptionné ─────────────────────────────────────── */}
            {order.status === "Réceptionné" && (
              <Callout tone="warning" icon="package" title="Linge réceptionné" style={{ marginTop: 12 }}>
                Votre linge a été réceptionné sur place. Nous allons le traiter sous peu.
              </Callout>
            )}

            {/* ─── Sur place : à récupérer ───────────────────────────────── */}
            {order.status === "Prêt" && order.order_type === "sur_place" && (
              <Callout tone="success" icon="store" title="Vos vêtements sont prêts" style={{ marginTop: 12 }}>
                Vous pouvez venir les récupérer sur place.
              </Callout>
            )}

            {/* ─── Commande récupérée ────────────────────────────────────── */}
            {order.status === "Récupéré" && (
              <Callout tone="success" icon="success" title="Commande récupérée" style={{ marginTop: 12 }}>
                Merci de votre confiance.
              </Callout>
            )}

            {/* ─── Créneau de livraison à choisir ────────────────────────── */}
            {order.status === "Prêt" && order.order_type !== "sur_place" && !order.delivery_date && (
              <Callout tone="info" icon="package" title="Vos vêtements sont prêts" style={{ marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span>Choisissez votre créneau de livraison.</span>
                  <Btn small icon="calendar" onClick={() => {
                    window.history.replaceState({}, "", `?delivery=${order.id}`);
                    window.location.reload();
                  }}>Choisir</Btn>
                </div>
              </Callout>
            )}

            {/* ─── Livraison programmée ──────────────────────────────────── */}
            {order.delivery_date && order.delivery_time && (
              <Callout tone="success" icon="delivery" title="Livraison programmée" style={{ marginTop: 12 }}>
                <span style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink, ...numeric }}>
                  {order.delivery_date} — {order.delivery_time}
                </span>
              </Callout>
            )}

            {/* ─── Contact ───────────────────────────────────────────────── */}
            <Card style={{ marginTop: 12 }}>
              <SectionHeader title="Contacter Setal Express" icon="chat" style={{ marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="white" icon="phone" onClick={callShop} style={{ flex: 1 }}>Appeler</Btn>
                <Btn variant="white" icon="chat" onClick={whatsappShop} style={{ flex: 1 }}>WhatsApp</Btn>
              </div>
            </Card>

            {/* ─── Carte du collecteur ───────────────────────────────────── */}
            {(order.status === "Collecté" || order.collector_status === "en_route") && (
              <div style={{ marginTop: 12 }}>
                <CollectorMap orderId={order.id} />
              </div>
            )}

            {/* ─── Notifications push ────────────────────────────────────── */}
            {order.status === "En attente" && "Notification" in window && "PushManager" in window && pushStatus !== "subscribed" && pushStatus !== "denied" && (
              <Callout tone="info" icon="bell" title="Être notifié au départ du collecteur" style={{ marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span>Suivez son trajet en temps réel sur la carte.</span>
                  <Btn small icon="bell" onClick={handlePushSubscribe}
                    disabled={pushStatus === "subscribing"} loading={pushStatus === "subscribing"}>
                    Activer
                  </Btn>
                </div>
              </Callout>
            )}
            {pushStatus === "subscribed" && order.status === "En attente" && (
              <Callout tone="success" icon="success" style={{ marginTop: 12 }}>
                Vous serez notifié quand votre collecteur partira.
              </Callout>
            )}

            {/* ─── Fidélité ──────────────────────────────────────────────── */}
            {loyalty && loyalty.total_orders > 0 && (
              <Card className="animate-fadeInUp" style={{ marginTop: 12 }}>
                <SectionHeader
                  title={t.loyaltyTitle}
                  icon="gift"
                  subtitle={`${loyalty.points || 0} points · ${loyalty.total_orders || 0} commande${loyalty.total_orders > 1 ? "s" : ""}`}
                />
                {loyalty.config?.enabled && loyalty.applicableDiscount > 0 && (
                  <Callout tone="success" icon="percent" style={{ marginBottom: 12 }}>
                    <strong style={numeric}>−{loyalty.applicableDiscount} %</strong> sur votre prochaine commande
                  </Callout>
                )}
                <LoyaltyProgress
                  points={loyalty.points}
                  totalOrders={loyalty.total_orders}
                  config={loyalty.config}
                />
              </Card>
            )}

            {/* ─── Reçu client ───────────────────────────────────────────── */}
            {order.items && order.price && (
              <div className="ticket animate-fadeInUp" style={{ marginTop: 12 }}>
                <div style={{ background: C.navy, color: C.inkOn, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, letterSpacing: ".08em" }}>
                    SETAL EXPRESS
                  </div>
                  <div style={{ fontSize: F.size.sm, color: "rgba(255,255,255,.72)", marginTop: 2 }}>
                    Pressing Premium — Dakar
                  </div>
                </div>

                <div style={{ padding: 16 }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 10, fontSize: F.size.sm, color: C.inkMuted,
                  }}>
                    <Ref style={{ fontSize: F.size.base }}>{order.id}</Ref>
                    <span style={numeric}>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString("fr-FR") : ""}
                    </span>
                  </div>

                  <div style={{ borderTop: `1px dashed ${C.borderStrong}`, margin: "10px 0" }} />

                  {order.items.split(",").filter(Boolean).map((item, i, arr) => {
                    const parts = item.trim().split(" x");
                    return (
                      <div key={i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        gap: 10, padding: "7px 0", fontSize: F.size.md,
                        borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none",
                      }}>
                        <span style={{ color: C.inkBody }}>{parts[0]?.trim()}</span>
                        {parts[1] && (
                          <Chip tone="navy" small>
                            <span style={numeric}>x{parts[1].trim()}</span>
                          </Chip>
                        )}
                      </div>
                    );
                  })}

                  <div style={{ borderTop: `1px dashed ${C.borderStrong}`, margin: "10px 0" }} />

                  {order.delivery_fee > 0 && (
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: 10, fontSize: F.size.base, color: C.inkMuted, marginBottom: 8,
                    }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Icon name="delivery" size={13} />
                        Livraison ({order.delivery_zone})
                      </span>
                      <span style={{ fontWeight: F.weight.medium, color: C.inkBody, ...numeric }}>
                        {Number(order.delivery_fee).toLocaleString()} F
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    gap: 10, paddingTop: 10, borderTop: `1px solid ${C.ink}`,
                  }}>
                    <span style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>TOTAL</span>
                    <span style={{ fontSize: F.size["2xl"], fontWeight: F.weight.bold, color: C.ink, ...numeric }}>
                      {Number(order.price).toLocaleString()} FCFA
                    </span>
                  </div>

                  {order.payment_status && (
                    <div style={{ marginTop: 10 }}>
                      <Chip
                        tone={order.payment_status === "paye" ? "success" : order.payment_status === "avance" ? "warning" : "danger"}
                        icon={order.payment_status === "paye" ? "success" : order.payment_status === "avance" ? "coins" : "alert"}
                      >
                        <span style={numeric}>
                          {order.payment_status === "paye"
                            ? "Payé"
                            : order.payment_status === "avance"
                              ? `Avance : ${Number(order.amount_paid || 0).toLocaleString()} FCFA`
                              : "Non payé"}
                        </span>
                      </Chip>
                    </div>
                  )}
                </div>

                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  borderTop: `1px solid ${C.border}`,
                }}>
                  <button onClick={downloadPDF} className="btn-bounce" style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                    padding: "12px 8px", background: C.surface, border: "none",
                    borderRight: `1px solid ${C.border}`, cursor: "pointer",
                    fontSize: F.size.base, fontWeight: F.weight.semi, color: C.navy,
                  }}>
                    <Icon name="download" size={15} /> Télécharger
                  </button>
                  <button onClick={sharePDF} className="btn-bounce" style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                    padding: "12px 8px", background: C.surface, border: "none",
                    cursor: "pointer", fontSize: F.size.base, fontWeight: F.weight.semi, color: C.navy,
                  }}>
                    <Icon name="share" size={15} /> Partager
                  </button>
                </div>
              </div>
            )}

            {/* ─── Confirmation client (livraison / récupération) ─────────── */}
            {(order.status === "Livré" || order.status === "Récupéré") && !order.client_confirmed_at && (
              <Callout
                tone="info"
                icon="list"
                title={order.status === "Récupéré" ? "Confirmez la récupération" : "Confirmez la réception"}
                style={{ marginTop: 12 }}
              >
                <div style={{ marginBottom: 10 }}>
                  {order.status === "Récupéré" ? "Avez-vous bien récupéré votre linge ?" : "Avez-vous bien reçu votre livraison ?"}
                </div>
                <Btn small variant="success" icon="check" onClick={async () => {
                  try {
                    await api.confirmDelivery(order.id);
                    const o = await api.getOrder(order.id);
                    setOrder(o);
                  } catch (err) { alert(err.message); }
                }}>
                  Oui, je confirme
                </Btn>
              </Callout>
            )}
            {(order.status === "Livré" || order.status === "Récupéré") && order.client_confirmed_at && (
              <Callout
                tone="success"
                icon="success"
                title={order.status === "Récupéré" ? "Récupération confirmée" : "Réception confirmée"}
                style={{ marginTop: 12 }}
              >
                Merci pour votre confiance.
              </Callout>
            )}

            {/* ─── Avis client ───────────────────────────────────────────── */}
            {(order.status === "Livré" || order.status === "Récupéré") && !reviewDone && (
              <Card style={{ marginTop: 12 }}>
                <SectionHeader
                  title="Comment était notre service ?"
                  subtitle="Votre avis nous aide à nous améliorer"
                  icon="star"
                />
                <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 14 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      title={`${n} étoile${n > 1 ? "s" : ""}`}
                      aria-label={`Noter ${n} sur 5`}
                      aria-pressed={reviewRating === n}
                      onClick={() => setReviewRating(n)}
                      style={{
                        padding: 5, background: "none", border: "none", cursor: "pointer",
                        color: reviewRating >= n ? C.amber : C.borderStrong,
                        transition: "color .15s ease",
                      }}
                    >
                      <Icon name="star" size={26} strokeWidth={reviewRating >= n ? 2 : 1.5} />
                    </button>
                  ))}
                </div>

                {reviewRating > 0 && (
                  <>
                    <Textarea
                      value={reviewComment}
                      onChange={setReviewComment}
                      rows={2}
                      placeholder="Un commentaire ? (optionnel)"
                    />
                    {reviewError && <Callout tone="danger" style={{ marginBottom: 12 }}>{reviewError}</Callout>}
                    <Btn fullWidth icon="send" disabled={reviewSubmitting} loading={reviewSubmitting} onClick={async () => {
                      setReviewSubmitting(true); setReviewError("");
                      try {
                        await api.submitReview({ order_id: order.id, phone: order.phone, rating: reviewRating, comment: reviewComment });
                        setReviewDone(true);
                      } catch (err) { setReviewError(err.message); }
                      finally { setReviewSubmitting(false); }
                    }}>
                      {reviewSubmitting ? "Envoi…" : "Envoyer mon avis"}
                    </Btn>
                  </>
                )}
              </Card>
            )}
            {(order.status === "Livré" || order.status === "Récupéré") && reviewDone && (
              <Callout tone="success" icon="heart" title="Merci pour votre avis" style={{ marginTop: 12 }}>
                Votre retour nous aide à améliorer nos services.
              </Callout>
            )}

            {/* ─── QR de suivi ───────────────────────────────────────────── */}
            <Card style={{ marginTop: 12 }}>
              <SectionHeader title={t.qrTitle} icon="qr" style={{ marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <QRCanvas orderId={order.id} size={132} />
                <p style={{ flex: 1, minWidth: 160, fontSize: F.size.base, color: C.inkMuted, lineHeight: 1.55 }}>
                  {t.qrDesc}
                </p>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export default Track;
